<template>
  <div class="chat-root">
    <div
      v-if="showChatView"
      id="chat-view"
      class="chat-view-modern"
    >
      <div
        id="chat"
        class="outline"
      >
        <div
          v-if="!isDaytimeAdvice"
          id="chat-header"
        >
          <div
            class="chat-title"
          >
            {{ chatTitleDefault }}
          </div>
        </div>
        <div
          id="chat-body"
        >
          <div
            id="chat-messages"
            :class="[unread ? 'new-notice' : '', isDaytimeAdvice ? 'daytime-advice-theme' : '']"
            @click="markAsRead"
          >
            <load-state-display
              :state="chatLogLoadState"
              :daytime-theme="isDaytimeAdvice"
              reload-button-id="chat-log-reload-button"
              @reload="loadChatLogs(true)"
            />
            <template
              v-if="isSuccess(chatLogLoadState)"
            >
              <div
                v-for="chatLog in chatLogs"
                :key="chatLog.id"
                class="chat-log"
                :class="sendByOthers(chatLog.senderType) ? 'others' : 'myself'"
              >
                <v-avatar
                  v-if="sendByOthers(chatLog.senderType)"
                  :color="isDaytimeAdvice ? 'orange-darken-1' : 'grey'"
                  :size="isDaytimeAdvice ? 44 : 40"
                  class="ma-2 mt-0"
                  :class="isDaytimeAdvice ? 'avatar-daytime-advice' : ''"
                  style="align-self: start"
                >
                  <v-img
                    v-if="user.lineIcon != null"
                    :src="user.lineIcon"
                  />
                  <span
                    v-if="user.lineIcon == null && user.name != null"
                    class="white--text headline"
                    :class="isDaytimeAdvice ? 'avatar-label-daytime-advice' : ''"
                  >{{ avatarDisplayLabel }}</span>
                  <span
                    v-if="user.name == null"
                    class="white--text headline"
                    :class="isDaytimeAdvice ? 'avatar-label-daytime-advice' : ''"
                  >削</span>
                </v-avatar>
                <div
                  v-if="chatLog.contentType == 'text' || chatLog.contentType == 'pdf'"
                  class="message-area"
                >
                  <div
                    v-if="chatLog.content"
                    v-linkify
                    class="sent-message"
                    v-html="$sanitize(newLineCodeToBr(chatLog.content))"
                  />
                  <div
                    v-if="chatLog.fileUrl && chatLog.contentType == 'pdf'"
                    class="sent-file"
                  >
                    <a
                      class="pink--text"
                      :href="`/api/v1/chat_logs/${chatLog.id}/media_download`"
                      target="_blank"
                    >
                      <v-icon color="pink">picture_as_pdf</v-icon>
                      {{ chatLog.fileName }}
                    </a>
                  </div>
                </div>
                <div
                  v-if="chatLog.contentType == 'video'"
                  class="sent-image pr-2"
                >
                  <video
                    :src="chatLog.content"
                    controls
                    controlsList="nodownload"
                    oncontextmenu="return false;"
                    class="filled-image"
                  />
                </div>
                <div
                  v-if="chatLog.contentType == 'image'"
                  class="sent-image pr-2"
                >
                  <div
                    v-if="chatLog.fileUrl"
                    class="chat-image-click-target"
                    role="button"
                    tabindex="0"
                    style="cursor: pointer; display: inline-block"
                    @click="openImageViewer(chatLog.fileUrl, chatLog.fileName)"
                    @keydown.enter.prevent="openImageViewer(chatLog.fileUrl, chatLog.fileName)"
                    @keydown.space.prevent="openImageViewer(chatLog.fileUrl, chatLog.fileName)"
                    @contextmenu.prevent
                    @selectstart.prevent
                    @mousedown.prevent
                    @dragstart.prevent
                  >
                    <img
                      :src="chatLog.fileUrl"
                      :alt="chatLog.fileName"
                      :title="chatLog.fileName"
                      class="filled-image secure-image"
                      draggable="false"
                    >
                  </div>
                  <div
                    v-if="chatLog.content"
                    class="chat-image-click-target"
                    role="button"
                    tabindex="0"
                    style="cursor: pointer; display: inline-block"
                    @click="openImageViewer(chatLog.content, chatLog.fileName || chatLog.content)"
                    @keydown.enter.prevent="openImageViewer(chatLog.content, chatLog.fileName || chatLog.content)"
                    @keydown.space.prevent="openImageViewer(chatLog.content, chatLog.fileName || chatLog.content)"
                    @contextmenu.prevent
                    @selectstart.prevent
                    @mousedown.prevent
                    @dragstart.prevent
                  >
                    <img
                      :src="chatLog.content"
                      :alt="chatLog.fileName || chatLog.content"
                      :title="chatLog.fileName || chatLog.content"
                      class="filled-image secure-image"
                      draggable="false"
                    >
                  </div>
                </div>
                <div class="sent-at pa-2">
                  <span v-if="chatLog.sentAtDate != null">{{ chatLog.sentAtDate }} <br></span>
                  <span>{{ chatLog.sentAtTime }}</span>
                </div>
              </div>
            </template>
            <div
              v-if="fileUrl"
              id="fileConfirm"
              class="chat-log myself"
            >
              <div class="message-area confirm">
                <div class="confirm-message">
                  この{{ imageFile(fileType) ? '画像' : 'ファイル' }}を送信しますか?<br>(まだ送信は完了していません)
                </div>
                <div v-if="imageFile(fileType)">
                  <img
                    :src="fileUrl"
                    class="filled-image"
                  >
                  <div>{{ fileName }}</div>
                </div>
                <div v-if="pdfFile(fileType)">
                  <v-btn
                    variant="outlined"
                    color="pink-lighten-1"
                    class="text-none"
                    size="large"
                  >
                    <v-icon>picture_as_pdf</v-icon>
                  </v-btn>
                  {{ fileName }}
                </div>
                <div class="flex xs12 sm12 md12">
                  <v-btn
                    size="small"
                    color="pink-lighten-1"
                    theme="dark"
                    :disabled="sending || isMessageDisabled"
                    @click="fileSend"
                  >
                    はい
                    <v-icon end>
                      mdi-send
                    </v-icon>
                  </v-btn>
                  <v-btn
                    size="small"
                    color="lighten-1"
                    :disabled="sending || isMessageDisabled"
                    @click="fileSendCancel"
                  >
                    いいえ
                    <v-icon end>
                      mdi-close-circle
                    </v-icon>
                  </v-btn>
                </div>
                <div
                  v-if="sendingImageError"
                  class="v-messages"
                  style="color: rgb(var(--v-theme-error))"
                >
                  {{ sendingImageErrorMessage }}
                </div>
              </div>
            </div>
          </div>
          <unassigned-room-warning-banner :visible="!isResponsibleDoctor" />
          <div id="chat-input">
            <chat-insert
              v-if="!isDaytimeAdvice"
              :log-type="logType"
              :message-disabled="isMessageDisabled"
              :line-url="room.lineUrl"
              :epds-url="epdsUrl"
              :doctor-name="doctorName"
              :doctor-profile-url="doctorProfileUrl"
              :department-type="departmentType"
              @insert-template="insertTemplate"
              @pick-file="pickFile"
            />
            <input
              ref="file"
              type="file"
              style="display: none"
              accept="image/*,application/pdf"
              @change="onFilePicked"
            >
            <div
              v-if="isDaytimeAdvice"
              id="chat-user-label"
            >
              <div class="chat-user-label-row">
                <v-icon
                  size="20"
                  class="chat-user-label-icon"
                >
                  mdi-account-circle
                </v-icon>
                <div
                  class="chat-title-daytime-advice"
                  :title="couponNamesTooltipText"
                >
                  <span class="chat-user-name">{{ chatTitleDaytimeAdviceName }}</span><span class="chat-user-meta">{{ chatTitleDaytimeAdviceMeta }}</span>
                </div>
                <div class="chat-user-quick-actions">
                  <button
                    type="button"
                    class="epds-tag-button"
                    :disabled="isMessageDisabled"
                    @click="sendDaytimeAdviceStartGreeting"
                  >
                    開始挨拶
                  </button>
                  <button
                    type="button"
                    class="epds-tag-button"
                    :disabled="isMessageDisabled"
                    @click="sendDaytimeAdviceEndGreeting"
                  >
                    終了挨拶
                  </button>
                  <button
                    type="button"
                    class="epds-tag-button"
                    :disabled="isMessageDisabled"
                    @click="sendEPDSTemplate"
                  >
                    EPDS
                  </button>
                  <button
                    type="button"
                    class="photo-attach-icon-button"
                    :disabled="isMessageDisabled"
                    aria-label="写真添付"
                    title="写真添付"
                    @click="pickFile"
                  >
                    <v-icon size="18">
                      mdi-paperclip
                    </v-icon>
                  </button>
                </div>
              </div>
              <div
                v-if="complaintPreview"
                class="chat-complaint"
              >
                {{ complaintPreview }}
              </div>
            </div>
            <div class="xs12 sm12 md12">
              <v-textarea
                id="message_input"
                v-model="sendMessage"
                auto-grow
                variant="solo"
                rows="2"
                max-rows="4"
                color="pink"
                :placeholder="messagePlaceholder"
                style="font-size: 14px; max-height: 14em; overflow-y: auto"
                :hide-details="sendingResultSuccess"
                :error-messages="sendingErrorMessage"
                :flat="true"
                :background-color="isMessageDisabled ? 'grey-lighten-4' : 'white'"
                :disabled="isMessageDisabled"
                @keydown.enter="send"
                @compositionstart="composing=true"
                @compositionend="composing=false"
                @click="markAsRead"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <media-viewer-dialog
      v-model="mediaViewer.open"
      :src="mediaViewer.src"
      :title="mediaViewer.title"
      :kind="mediaViewer.kind"
      @error="onMediaViewerError"
    />

    <unassigned-room-confirm-dialog
      v-model:open="showUnassignedConfirmDialog"
      @confirm="onConfirmUnassignedSend"
      @cancel="onCancelUnassignedSend"
    />
  </div>
</template>

<script>
const context = new AudioContext();
import { mapGetters, mapActions } from "vuex";
import timestampMixin from "../../mixins/timestamp.js";
import audioMixin from "../../mixins/audio.js";
import commonMixin from "../../mixins/chat_log_common.js";
import loadStateMixin from "../../mixins/load_state.js";
import LoadStateDisplay from "../shared/LoadStateDisplay.vue";
import ChatInsert from "./ChatInsert.vue";
import { LOAD_STATES } from "../../constants/load_states";
import { createKarteImageListRefresher } from "../../features/karte-image-list-refresh";
import {
  MEDIA_VIEWER_KINDS,
  MediaViewerDialog,
  createMediaViewerState,
  handleMediaViewerError,
  openMediaViewer,
} from "../../features/media-viewer";
import {
  isResponsibleDoctor,
  UnassignedRoomWarningBanner,
  UnassignedRoomConfirmDialog,
} from "../../features/unassigned-room-guard";

export default {
  name: "Chat",
  components: {
    LoadStateDisplay,
    ChatInsert,
    MediaViewerDialog,
    UnassignedRoomWarningBanner,
    UnassignedRoomConfirmDialog,
  },
  mixins: [timestampMixin, audioMixin, commonMixin, loadStateMixin],
  props: {
    chatId: {
      type: Number,
      required: true
    },
    logType: {
      type: String,
      required: true
    },
    messageDisabled: {
      type: Boolean,
      required: true
    },
    departmentType: {
      type: Number,
      required: true
    },
    complaint: {
      type: String,
      required: false,
      default: ""
    },
    couponName: {
      type: String,
      required: false
    },
    couponNames: {
      type: Array,
      required: false
    },
    shiftDoctorId: {
      type: Number,
      required: false,
      default: undefined
    },
  },
  emits: ["afterSendingChat"],
  data() {
    return {
      sendMessage: "",
      messageChannel: null,
      composing: false,
      fileName: "",
      fileUrl: "",
      fileType: "",
      showChatView: true,
      chatMessageStyle: "",
      unread: false,
      scrollHeight: 0,
      oldsScrollHeight: 0,
      calcScrollHeightIntervalId: null,
      sending: false,
      sendingResultSuccess: true,
      sendingErrorMessage: null,
      chatLogLoadState: null,
      chatLogTimerId: null,
      sendingImageErrorMessage: null,
      sendingImageError: false,
      localMessageDisabled: false,

      // 夜間相談チャット 担当外ルーム誤送信防止機能
      permissionGranted: false,
      pendingTextSend: null,
      showUnassignedConfirmDialog: false,

      karteImageListRefresher: null,
      pendingKarteImagesRefreshLogId: null,
      lastKarteImagesRefreshLogId: null,
      lastKarteImagesRefreshErrorNotifiedLogId: null,

      mediaViewer: createMediaViewerState(),
    };
  },

  computed: {
    ...mapGetters([
      "currentDoctor",
      "doctorName",
      "doctorProfileUrl",
      "chatLogs",
      "hasUnreadMessages",
      "latestMessageId",
      "room",
      "user",
      "epdsUrl",
      "hasAllCapabilities"
    ]),
    isDaytimeAdvice: function() {
      return this.logType === "daytimeAdvice";
    },
    userDisplayName: function() {
      return this.user?.lineName != null ? this.user.lineName : this.user?.name;
    },
    avatarDisplayLabel: function() {
      const name = this.user?.name;
      if (!name) return "";
      if (this.isDaytimeAdvice) {
        // 「姓 名」形式の場合は姓のみをアバターに表示する
        return name.trim().split(/\s+/)[0];
      }
      return name.slice(0, 1);
    },
    normalizedCouponNames: function() {
      const hasCouponNamesProp = this.couponNames != null;
      const fromArray = Array.isArray(this.couponNames) ? this.couponNames : [];
      const base = hasCouponNamesProp ? fromArray : (this.couponName != null ? [this.couponName] : []);
      const seen = new Set();
      const result = [];
      for (const value of base) {
        const name = (value ?? "").toString().trim();
        if (!name) continue;
        if (seen.has(name)) continue;
        seen.add(name);
        result.push(name);
      }
      return result;
    },
    couponNamesDisplayText: function() {
      return this.normalizedCouponNames.join("、");
    },
    couponNamesDisplayTextTruncated: function() {
      const text = this.couponNamesDisplayText;
      if (!text) return "";
      // 15文字以上なら15文字目で切り捨てて末尾に"..."を付与
      if (text.length >= 15) return `${text.slice(0, 15)}...`;
      return text;
    },
    couponNamesTooltipText: function() {
      // 仕様: 「ユーザーが保持しているすべての有効な導入先名称」をフルテキストで表示
      return this.normalizedCouponNames.join("\n");
    },
    chatTitleDefault: function() {
      const name = this.userDisplayName || "";
      const id = this.user?.id != null ? this.user.id : "";
      const couponSuffix = this.couponName != null ? `/${this.couponName}` : "";
      return `${name} （ID: ${id}${couponSuffix}）`;
    },
    chatTitleDaytimeAdviceName: function() {
      return this.userDisplayName || "-";
    },
    chatTitleDaytimeAdviceMeta: function() {
      const id = this.user?.id != null ? this.user.id : "-";
      const coupon = this.couponNamesDisplayTextTruncated || "-";
      return `（ID:${id} / ${coupon}）`;
    },
    complaintPreview: function() {
      if (!this.isDaytimeAdvice) return "";
      const raw = (this.complaint || "").replace(/\s+/g, " ").trim();
      if (!raw) return "";
      if (raw.length <= 10) return raw;
      return `${raw.slice(0, 10)}...`;
    },
    messagePlaceholder: function() {
      if (!this.isDaytimeAdvice) return "message here";
      const name = this.userDisplayName || "";
      const id = this.user?.id != null ? this.user.id : "";
      const parts = [];
      if (name) parts.push(name);
      if (id) parts.push(`ID: ${id}`);
      const prefix = parts.join(" ").trim();
      if (!prefix) return "送信する";
      return `${prefix} に送信する`;
    },
    isMessageDisabled: function() {
      return this.messageDisabled || this.localMessageDisabled;
    },
    isResponsibleDoctor: function() {
      if (this.logType !== "karte") return true; // 日中相談等は対象外（スコープ外）
      return isResponsibleDoctor({
        isOperator: this.hasAllCapabilities(["operator:access"]),
        shiftDoctorId: this.shiftDoctorId,
        currentDoctorId: this.currentDoctor?.id,
      });
    },
    shouldGuardTextSend: function() {
      return !this.isResponsibleDoctor && !this.permissionGranted;
    },
  },

  watch: {
    "$route" () {
      this.pendingKarteImagesRefreshLogId = null;
      if (this.karteImageListRefresher != null) {
        this.karteImageListRefresher.dispose();
      }

      this.initialize();
      this.clearChatInput();
      // 新着通知のリセット
      this.unread = false;
    },
    scrollHeight (val){
      if( this.oldScrollHeight < val ){
        this.scrollToEnd();
      }
      this.oldScrollHeight = this.scrollHeight;
    },
    departmentType: {
      handler: "loadEpdsUrl",
      immediate: true
    },
  },

  created() {
    this.karteImageListRefresher = createKarteImageListRefresher({
      debounceMs: () => this.karteImagesRefreshDebounceMs(),
      maxAttempts: () => this.karteImagesRefreshMaxAttempts(),
      retryDelayMs: (nextAttempt) => this.karteImagesRefreshRetryDelayMs(nextAttempt),
      getCurrentReservationId: () => this.chatId,
      fetch: (reservationId) => this.fetchKarteImages({ reservation_id: reservationId }),
    });
    this.initialize();
  },

  mounted () {
    this.calcScrollHeightIntervalId = setInterval(this.calcScrollHeight, 200);
  },

  beforeUnmount () {
    clearTimeout(this.chatLogTimerId);
    if (this.karteImageListRefresher != null) {
      this.karteImageListRefresher.dispose();
    }
    clearInterval(this.calcScrollHeightIntervalId);
    this.disConnectionChannel();
  },

  methods: {
    ...mapActions(["markAsRead", "fetchKarteImages"]),
    openImageViewer(src, title) {
      openMediaViewer(this.mediaViewer, {
        src,
        title: title || "",
        kind: MEDIA_VIEWER_KINDS.IMAGE,
      });
    },
    onMediaViewerError() {
      handleMediaViewerError(this.mediaViewer);
    },
    karteImagesRefreshDebounceMs() {
      // karte_imagesの保存はサーバ側処理を挟むため、受信直後に叩くと反映前のことがある。
      // そのため短いデバウンスを入れてから再取得する。
      return 800;
    },
    isAttachmentContentType(contentType) {
      return ["image", "pdf", "video"].includes(contentType);
    },
    karteImagesRefreshMaxAttempts() {
      // 初回 + リトライ2回 = 合計3回
      return 3;
    },
    karteImagesRefreshRetryDelayMs(nextAttempt) {
      // 2回目: 500ms, 3回目: 1000ms
      return Math.max(0, (nextAttempt - 1) * 500);
    },
    notifyKarteImagesRefreshError(log) {
      if (!log) return;
      if (log.id != null && log.id === this.lastKarteImagesRefreshErrorNotifiedLogId) return;

      if (log.id != null) {
        this.lastKarteImagesRefreshErrorNotifiedLogId = log.id;
      }

      if (typeof window !== "undefined" && typeof window.alert === "function") {
        window.alert("添付ファイルのサムネイル一覧の更新に失敗しました。ページを再読み込みしてください。");
      }
    },
    shouldRefreshKarteImagesForLog(log) {
      if (this.logType !== "karte") return false;
      if (!log) return false;
      if (!this.isAttachmentContentType(log.contentType)) return false;
      if (this.chatId == null) return false;

      // 同一イベントや短時間の連続受信での過剰再取得を抑止
      if (log.id != null && log.id === this.lastKarteImagesRefreshLogId) return false;
      if (log.id != null && log.id === this.pendingKarteImagesRefreshLogId) return false;

      return true;
    },
    queueRefreshKarteImages(log) {
      if (!this.shouldRefreshKarteImagesForLog(log)) return;
      if (log.id != null) {
        this.pendingKarteImagesRefreshLogId = log.id;
      }

      const reservationId = this.chatId;
      if (this.karteImageListRefresher == null) return;

      this.karteImageListRefresher.queueRefresh({
        reservationId,
        onDebouncedStart: () => {
          const refreshLogId = this.pendingKarteImagesRefreshLogId;
          this.pendingKarteImagesRefreshLogId = null;
          if (refreshLogId != null) {
            this.lastKarteImagesRefreshLogId = refreshLogId;
          }
        },
        onFinalFailure: () => {
          this.notifyKarteImagesRefreshError(log);
        },
      });
    },
    initialize(){
      this.localMessageDisabled = false;
      // ルーム/カルテ切替時に担当外送信の許可状態をリセットする
      this.permissionGranted = false;
      this.pendingTextSend = null;
      this.showUnassignedConfirmDialog = false;
      this.disConnectionChannel();
      this.connectionChannel();
      this.loadChatLogs(true);
    },
    calcScrollHeight() {
      var scrollComponent = document.getElementById("chat-messages");
      if( scrollComponent != null ){
        this.scrollHeight = scrollComponent.scrollHeight;
      }
    },
    connectionChannel() {
      console.log(this.timestamp() + " connection Message Channel.");
      var that = this;
      this.messageChannel = this.$cable.subscriptions.create(
        {
          channel: "ChatMessageChannel",
          chat_id: this.chatId,
          log_type: this.logType,

        },
        {
          received: (data) =>  {
            console.log(that.timestamp() + " received karte message.");
            var log = JSON.parse(data["chat_log"]);
            log["logType"] = this.logType;
            that.$store.dispatch("addChatLog", log).then(()=>{
              if( log.readAt == null ){ // not myself
                if(that.windowIsNotActive()){
                  that.soundNotify();
                }
                that.unread = true;
              }
            }).finally(() => {
              that.queueRefreshKarteImages(log);
            });
          },
          connected: function() {
            console.log(that.timestamp() + " connected message channel.");
            that.loadChatLogs(false);
          },
          disconnected: function() {
            console.log(that.timestamp() + " disconnected message channel.");
          },
        }
      );
    },
    disConnectionChannel() {
      if (this.messageChannel != null && typeof this.messageChannel.unsubscribe === "function") {
        this.messageChannel.unsubscribe();
      }
      this.messageChannel = null;
    },
    send(e) {
      if (this.isMessageDisabled) return;

      if (!this.composing && !this.sending) {
        // Alt + Enter/ Cmd + Enter の場合のみ送信する
        if(e.altKey === true || e.metaKey === true){
          e.preventDefault();
          if(this.sendMessage.replace(/\s/g, "").length > 0){
            this.attemptSendText(this.sendMessage);
          }
        }
      }
    },
    attemptSendText(content) {
      const params = {
        chatId: this.chatId,
        content,
        logType: this.logType,
        contentType: "text",
      };

      if (this.shouldGuardTextSend) {
        // 担当外・未許可の場合は即時送信せず確認ダイアログを表示する
        this.pendingTextSend = params;
        this.showUnassignedConfirmDialog = true;
        return;
      }

      this.callPostApi(params);
    },
    onConfirmUnassignedSend() {
      this.showUnassignedConfirmDialog = false;
      this.permissionGranted = true; // 以降このコンテキストでは確認を省略
      if (this.pendingTextSend) {
        this.callPostApi(this.pendingTextSend);
        this.pendingTextSend = null;
      }
    },
    onCancelUnassignedSend() {
      this.showUnassignedConfirmDialog = false;
      this.pendingTextSend = null; // sendMessageには触れないため入力内容は保持される
    },
    sendByOthers(senderType){
      return senderType == "user";
    },
    pickFile(){
      if (this.isMessageDisabled) return;

      this.clearFile();
      this.$refs.file.click();
    },
    onFilePicked(e) {
      if (this.isMessageDisabled) return;

      const files = e.target.files;
      if(files[0] !== undefined) {
        this.fileName = files[0].name;
        this.fileType = files[0].type;
        if(this.fileName.lastIndexOf(".") <= 0) {
          // ファイルじゃない
          return;
        }
        const fr = new FileReader ();
        fr.readAsDataURL(files[0]);
        fr.addEventListener("loadend", () => {
          this.fileUrl = fr.result; // base64
        });
      }
      e.target.value = "";
    },
    fileSend(){
      if (this.isMessageDisabled) return;

      this.callPostApi({
        chatId: this.chatId,
        fileUrl: this.fileUrl,
        fileName: this.fileName,
        contentType: this.imageFile(this.fileType) ? "image" : "pdf",
        logType: this.logType,
      });
    },
    callPostApi(params) {
      if (this.isMessageDisabled) return;

      this.sending = true;
      this.$store.dispatch(
        "post", params
      ).then(()=>{
        // 送信成功時の処理
        this.sendMessage = "";
        this.sendingResultSuccess = true;
        this.clearFile();
        this.clearSendingStatus();
        this.clearImageSendingStatus();

        // 長い文を送った場合ログが更新されないことがあるため
        this.loadChatLogs(false);

        // 親要素でチャット送信後に行いたい処理を実行する
        this.$emit("afterSendingChat");

        this.sending = false;
      }).catch((error) => {
        console.log("送信失敗:", error);
        this.sending = false;
        if (this.isBlockingChatError(error)) {
          if (typeof window !== "undefined" && typeof window.alert === "function") window.alert(error.message);
          this.localMessageDisabled = true;
          this.clearFile();
          return;
        }

        // 送信失敗時の処理
        if (params.contentType == "text"){
          this.sendingResultSuccess = false;
          this.sendingErrorMessage = "送信に失敗しました。もう一度送信してください。";
        } else {
          this.sendingImageError = true;
          this.sendingImageErrorMessage = "送信に失敗しました。もう一度送信してください。";
        }
        // ユーザーが退会している可能性があるのでログを再取得する
        this.loadChatLogs(false);
      });
    },
    isBlockingChatError(error) {
      return this.logType === "karte" && error?.status === 400 && !!error?.message;
    },
    clearSendingStatus(){
      this.sendingResultSuccess = true;
      this.sendingErrorMessage = "";
    },
    clearImageSendingStatus(){
      this.sendingImageError = false;
      this.sendingImageErrorMessage = "";
    },
    fileSendCancel(){
      if (this.isMessageDisabled) return;

      this.clearFile();
    },
    clearFile(){
      this.fileUrl = "";
      this.fileName = "";
      this.fileType = "";
      this.clearImageSendingStatus();
    },
    scrollToEnd(){
      var scrollArea = document.getElementById("chat-messages");
      scrollArea.scrollTop = scrollArea.scrollHeight;
    },
    markAsRead(){
      this.$store.dispatch("markAsRead", {
        chatId: this.chatId,
        logType: this.logType
      }).catch((error) => {
        console.error("markAsRead failed", error); 
      });
      this.unread = false;
    },
    loadChatLogs(beforeClear){
      if(beforeClear){
        this.$store.dispatch("clearChatLogs");
        this.chatLogLoadState = LOAD_STATES.LOADING;
      }
      var savedLatestId = this.latestMessageId;
      
      clearTimeout(this.chatLogTimerId);
      this.chatLogTimerId = setTimeout(() => {
        this.chatLogLoadState = LOAD_STATES.ERROR;
      }, 30 * 1000);

      this.$store.dispatch("fetchChatLogs", {
        chatId: this.chatId,
        logType: this.logType
      }).then(()=>{
        var hasNewMessage = this.latestMessageId != savedLatestId;
        if(this.unread && hasNewMessage && this.windowIsNotActive()){
          this.soundNotify();
        }
        this.chatLogLoadState = LOAD_STATES.SUCCESS;
      }).catch(() => {
        this.chatLogLoadState = LOAD_STATES.ERROR;
      }).finally(() => {
        clearTimeout(this.chatLogTimerId);
      });
    },
    loadEpdsUrl() {
      if(this.departmentType != null){
        this.$store.dispatch("fetchEpdsUrl", {
          department_type: this.departmentType
        });
      }
    },
    insertTemplate(message){
      if (this.isMessageDisabled) return;

      this.sendMessage = message;
    },
    sendEPDSTemplate(){
      if (this.isMessageDisabled) return;

      let fixedPhrase = "産後はどの時期も体と心の疲労が溜まりやすい時期ですので、みなさんにアンケートとしてお願いしております。今のご自身の気持ちを振り返ってみましょう。\n";
      fixedPhrase += "ぜひ下記URLより回答してみてくださいね。\n\n";
      fixedPhrase += this.epdsUrl;
      this.insertTemplate(fixedPhrase);
    },
    sendDaytimeAdviceStartGreeting(){
      if (this.isMessageDisabled) return;

      let fixedPhrase = "本日は本サービスを利用いただきありがとうございます。\n";
      fixedPhrase += `本日担当します助産師の${this.doctorName}です。\n`;
      fixedPhrase += "どうぞ、よろしくお願いいたします。\n";
      fixedPhrase += this.doctorProfileUrl;
      this.insertTemplate(fixedPhrase);
    },
    sendDaytimeAdviceEndGreeting(){
      if (this.isMessageDisabled) return;

      let fixedPhrase = `本日は助産師の${this.doctorName}が担当させていただきました。\n`;
      fixedPhrase += "ご利用いただき、ありがとうございました。";
      this.insertTemplate(fixedPhrase);
    },
    imageFile(contentType){
      return contentType.match(/image\/.*/);
    },
    pdfFile(contentType){
      return contentType.match(/application\/pdf/);
    },
    windowIsNotActive(){
      return !document.hasFocus() || document.activeElement.getAttribute("id") != "message_input";
    },
    // for debug
    timestamp(){
      var now = new Date();
      return now.getFullYear() + "/" + (now.getMonth()+1) + "/" + now.getDate() + " " + now.getHours() + ":" + now.getMinutes() + ":" + now.getSeconds();
    },
    clearChatInput(){
      this.sendMessage = "";
    },
  },
};
</script>

<style lang="scss" scoped>
// このdiv自体はボックスを生成させず（display: contents）、#chat-viewが親要素
// （.karte-scroll-area-y 等、高さが確定している要素）の直接の子として扱われるようにする。
// こうしないと #chat-view の height: 100% が解決できず、全体の高さが正しく固定されない。
.chat-root {
  display: contents;
}

#chat-view {
  // 親要素（.karte-scroll-area-y 等）が上部ヘッダー・下部固定アクションバー分を
  // 差し引いた高さをすでに提供しているため、そのまま引き継ぐ（固定値によるズレを避け、
  // 下部固定アクションバーと重ならないことを構造的に保証する）。
  height: 100%;
  display: flex;
  flex-direction: column;
}

.chat-view-modern {
  padding: 0;
  padding-bottom: 8px;
  opacity: 1;
  box-sizing: border-box;
  z-index: 100001;
  min-height: 0;

  width: auto;
  color: #424242;
}

#chat.outline {
  background: #fff;
  border: 1px solid #e0e0e0;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

#chat-header {
  position: relative;
  z-index: 100;
  min-height: 40px;
  flex-shrink: 0;
  width: 100%;
  padding: 0 16px;
  border-bottom: 1px solid #e0e0e0;
  color: #757575;
}

.new-notice {
  box-sizing: border-box;
  border: 2px solid;
  animation: notice-border 1.5s infinite;
  -webkit-animation: notice-border 1.5s infinite;
}

.chat-title {
  font-size: 14px;
  line-height: 20px;
  min-height: 40px;
  margin-right: 60px;
  overflow-x: hidden;
  width: 100%;
  padding: 10px 4px;
}

#chat-user-label {
  border-bottom: 1px solid #e0e0e0;
  color: #4a4a4a;
  padding: 6px 10px;
  // 親要素（チャット幅）に応じてラベル/テンプレートボタンのレイアウトを切り替える
  container-type: inline-size;
  container-name: chat-user-label;
}

.chat-complaint {
  color: #999;
  font-size: 12px;
  line-height: 16px;
  padding: 0 4px 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

#chat-body {
  padding-bottom: 0px;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

#chat-messages {
  flex: 1 1 auto;
  min-height: 0;
  overscroll-behavior-y: contain;
  overflow: scroll;
  padding: 10px 5px;
  // メッセージ吹き出しの幅をこの要素の幅基準で動的に変える
  container-type: inline-size;
  container-name: chat-messages;
}

.chat-log {
  clear: both;
  padding-top: 5px;
  width: 100%;
  overflow: hidden;
  word-wrap: break-word;

  /* flex box */
  display: -webkit-flex;
  display: -moz-flex;
  display: -ms-flex;
  display: -o-flex;
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-end;

  .message-area {
    padding: 15px;
    border: 1px solid;
    margin-bottom: 5px;
    // 狭い時は250pxを上限とし、広い時はメッセージ欄幅の半分以上（最大70%）まで広げる
    max-width: min(640px, max(250px, 70cqw));

  }

  .sent-at {
    color: #999;
    font-size: 10px;
  }

  .sent-message {
    word-break: break-all;
  }

  .confirm {
    .filled-image {
      max-width: fit-content;
    }
  }
}

.others {
  padding-right: 30px;
  flex-direction: row;

  .message-area {
    border-color: #e0e0e0;
    border-radius: 0 20px 20px 20px;
    padding-left: 10px;
  }
}

.myself {
  padding-left: 30px;
  flex-direction: row-reverse;

  .message-area {
    border-color: #FFD1E0;
    background-color: #FFD1E0;
    border-radius: 20px 0 20px 20px;
    padding-right: 10px;
  }

  .sent-at {
    text-align: right;
  }
}

// 日中助産師相談チャット専用テーマ
// LINE公式アカウント風の配色・吹き出し・ラベルUIに変更する
.daytime-advice-theme {
  background-color: #8891c5;
  padding: 12px 10px;

  .chat-log {
    padding-top: 12px;
  }

  .others {
    .message-area {
      border: none;
      background-color: #fff;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
    }

    .sent-at {
      color: #fff;
    }
  }

  .myself {
    .message-area {
      border: none;
      border-color: #8BC34A;
      background-color: #8BC34A;
      color: #212121;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
    }

    .sent-at {
      color: #fff;
    }
  }

  .avatar-daytime-advice {
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
  }

  .avatar-label-daytime-advice {
    font-size: 12px !important;
    line-height: 1.2;
    font-weight: 700;
    letter-spacing: 0;
  }
}

.chat-user-label-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  flex-wrap: wrap;
}

.chat-user-label-icon {
  flex-shrink: 0;
  color: #757575;
  margin-top: 2px;
}

.chat-title-daytime-advice {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding-top: 2px;
}

.chat-user-name {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-user-meta {
  flex: 0 0 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-user-quick-actions {
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: repeat(2, max-content);
  justify-content: start;
  gap: 4px 6px;
}

// 親要素の幅が十分にある場合は、氏名とID/クーポンを1行に、テンプレートボタンも横一列にする
@container chat-user-label (min-width: 480px) {
  .chat-title-daytime-advice {
    flex-direction: row;
    align-items: baseline;
    gap: 4px;
  }

  .chat-user-quick-actions {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    gap: 6px;
  }
}

.epds-tag-button {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border: none;
  border-radius: 999px;
  padding: 2px 12px;
  font-size: 12px;
  font-weight: 400;
  color: #D72E4B;
  background-color: #FFEEF1;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// 夜間相談の写真添付アイコン（ChatInsert.vue）と統一感を持たせ、テンプレートボタン
// (ピル型・ピンク背景)とは異なるシンプルなアイコンのみのデザインにする。
.photo-attach-icon-button {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: #757575;
  padding: 4px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

#chat-input {
  flex-shrink: 0;
  background-color: #fff;
  :deep(.v-textarea textarea) {
    font-size: 0.875rem !important;
  }
  :deep(#message_input-messages) {
    margin-bottom: 5px !important;
  }
}

.filled-image {
  width: 200px;
  height: auto;
}

@-webkit-keyframes notice-border {
  0%   { border-color: #fff1f6; }
  20%  { border-color: #f48bae; }
  40%  { border-color: #ef5d8d; }
  60%  { border-color: #ec407a; }
  80%  { border-color: #ef5d8d; }
  100% { border-color: #fff1f6; }
}
@keyframes notice-border {
  0%   { border-color: #fff1f6; }
  20%  { border-color: #f48bae; }
  40%  { border-color: #ef5d8d; }
  60%  { border-color: #ec407a; }
  80%  { border-color: #ef5d8d; }
  100% { border-color: #fff1f6; }
}
</style>
